import { Component, DestroyRef, inject, ViewChild } from '@angular/core';
import { Story } from '../../model/story/story.model';
import { GoogleMap, MapInfoWindow, MapMarker, MapMarkerClusterer, Renderer } from '@angular/google-maps';
import { AppStateStore } from '../../store/app-state.store';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { StoryMarkerData } from '../../model/story/story-marker-data.interface';
import { MatButton } from '@angular/material/button';
import { Platform } from '@angular/cdk/platform';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { NavigationOptions } from '../navigation-options/navigation-options';
import { AsyncPipe, NgClass } from '@angular/common';
import { ResponsiveService } from '../../services/responsive.service';

@Component({
  selector: 'app-story-map',
  imports: [
    GoogleMap,
    MapMarker,
    MapInfoWindow,
    MapMarkerClusterer,
    MatButton,
    RouterLink,
    AsyncPipe,
    NgClass,
  ],
  templateUrl: './story-map.html',
  styleUrl: './story-map.scss',
})
export class StoryMap {
  @ViewChild(MapInfoWindow) infoWindow!: MapInfoWindow;
  @ViewChild(GoogleMap) map!: GoogleMap;
  /**
   * Dependencies
   */
  private readonly appStateStore = inject(AppStateStore);
  private readonly destroyRef = inject(DestroyRef)
  private readonly matBottomSheet = inject(MatBottomSheet);
  public readonly platform = inject(Platform);
  public readonly responsiveService = inject(ResponsiveService);
  /**
   * Data
   */
  public markerList: StoryMarkerData[] = [];
  public infoWindowStory: Story = Story.createFromApi();
  /**
   * Geolocation
   */
  public userLocation: google.maps.LatLngLiteral | null = null;
  public userLocationMarker: google.maps.MarkerOptions | null = null;
  /**
   * Map
   */
  public mapCenter = {lat: 51.9225, lng: 4.47917}; // Rotterdam
  public mapOptions: google.maps.MapOptions = {
    center: this.mapCenter,
    zoom: 12,
    mapTypeControl: false,
    fullscreenControl: true,
    streetViewControl: false,
    gestureHandling: "greedy",
    backgroundColor: "#F5877C",
    styles: [
      // Road network styling - white roads
      {
        featureType: "road",
        elementType: "geometry",
        stylers: [
          {color: "#ffffff"},
          {visibility: "on"},
        ],
      },
      {
        featureType: "road",
        elementType: "labels.text.fill",
        stylers: [
          {color: "#706f6f"},
        ],
      },
      {
        featureType: "road.arterial",
        elementType: "geometry",
        stylers: [
          {color: "#ffffff"},
        ],
      },
      {
        featureType: "road.highway",
        elementType: "geometry",
        stylers: [
          {color: "#ffffff"},
        ],
      },
      {
        featureType: "road.local",
        elementType: "geometry",
        stylers: [
          {color: "#ffffff"},
        ],
      },
      // Urban areas - coral color
      {
        featureType: "landscape.man_made",
        elementType: "geometry",
        stylers: [
          {color: "#fac3bc"},
        ],
      },
      // Hide road icons and direction arrows
      {
        featureType: "road",
        elementType: "labels.icon",
        stylers: [
          {visibility: "off"},
        ],
      },
      // Hide transit stations
      {
        featureType: "transit.station",
        elementType: "labels",
        stylers: [
          {visibility: "off"},
        ],
      },
      // Hide all points of interest
      {
        featureType: "poi",
        elementType: "geometry",
        stylers: [
          {visibility: "off"},
        ],
      },
      {
        featureType: "poi",
        elementType: "labels",
        stylers: [
          {visibility: "off"},
        ],
      },
      // Hide recreational POI
      {
        featureType: "poi.attraction",
        elementType: "geometry",
        stylers: [
          {visibility: "off"},
        ],
      },
      {
        featureType: "poi.attraction",
        elementType: "labels",
        stylers: [
          {visibility: "off"},
        ],
      },
      // Hide retail/business POI
      {
        featureType: "poi.business",
        elementType: "geometry",
        stylers: [
          {visibility: "off"},
        ],
      },
      {
        featureType: "poi.business",
        elementType: "labels",
        stylers: [
          {visibility: "off"},
        ],
      },
      // Hide medical POI
      {
        featureType: "poi.medical",
        elementType: "geometry",
        stylers: [
          {visibility: "off"},
        ],
      },
      {
        featureType: "poi.medical",
        elementType: "labels",
        stylers: [
          {visibility: "off"},
        ],
      },
      // Hide government POI
      {
        featureType: "poi.government",
        elementType: "geometry",
        stylers: [
          {visibility: "off"},
        ],
      },
      {
        featureType: "poi.government",
        elementType: "labels",
        stylers: [
          {visibility: "off"},
        ],
      },
      // Hide parks and recreational areas
      {
        featureType: "poi.park",
        elementType: "geometry",
        stylers: [
          {visibility: "off"},
        ],
      },
      {
        featureType: "poi.park",
        elementType: "labels",
        stylers: [
          {visibility: "off"},
        ],
      },
      // Hide places of worship
      {
        featureType: "poi.place_of_worship",
        elementType: "geometry",
        stylers: [
          {visibility: "off"},
        ],
      },
      {
        featureType: "poi.place_of_worship",
        elementType: "labels",
        stylers: [
          {visibility: "off"},
        ],
      },
      // Hide schools
      {
        featureType: "poi.school",
        elementType: "geometry",
        stylers: [
          {visibility: "off"},
        ],
      },
      {
        featureType: "poi.school",
        elementType: "labels",
        stylers: [
          {visibility: "off"},
        ],
      },
      // Hide sports complexes
      {
        featureType: "poi.sports_complex",
        elementType: "geometry",
        stylers: [
          {visibility: "off"},
        ],
      },
      {
        featureType: "poi.sports_complex",
        elementType: "labels",
        stylers: [
          {visibility: "off"},
        ],
      },
    ],
  };

  onMapReady() {
    this.listenToStoryChanges();
    this.requestGeolocation();
  }

  private listenToStoryChanges(): void {
    this.appStateStore.storyList$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((storyList: Story[]): void => {
        this.markerList = storyList.map(story => ({
          story: story,
          position: {
            lat: parseFloat(story.latitude),
            lng: parseFloat(story.longitude),
          },
          options: {
            title: story.title,
            icon: {
              path: google.maps.SymbolPath.CIRCLE,
              scale: 12,
              fillColor: story.isActive ? (story.isProject ? "#592b11" : "#f22e89") : "#a18a94",
              fillOpacity: 1,
              strokeColor: "#fff",
              strokeWeight: 2,
            },
          },
        }));
      });
  }

  private centerMapToMarkers() {
    const bounds = new google.maps.LatLngBounds();
    if (this.markerList.length > 1) {
      this.markerList.forEach((storyMarkerData: StoryMarkerData) => bounds.extend(storyMarkerData.position));
      this.map.googleMap!.fitBounds(bounds, {top: 50, bottom: 50, left: 50, right: 50});
    } else {
      this.mapCenter = {
        lat: parseFloat(this.infoWindowStory.latitude),
        lng: parseFloat(this.infoWindowStory.longitude),
      };
    }

  }

  // Custom cluster rendering (color + size based on marker count)
  public customClusterRenderer: Renderer = {
    render: ({count, position}) => {
      let color = '#0080f7ff';
      let size = 36;

      if (count >= 7) {
        color = '#F59E0B';
        size = 42;
      }

      if (count >= 15) {
        color = '#EF4444';
        size = 46;
      }

      color = '#f22e89';

      return new google.maps.Marker({
        position,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: size / 3,
          fillColor: color,
          fillOpacity: 0.85,
          strokeColor: '#FFFFFF',
          strokeWeight: 2,
        },
        label: {
          text: String(count),
          color: 'white',
          fontSize: '14px',
          fontWeight: '700',
        },
        title: `Cluster of ${count} markers`,
        zIndex: 1000 + count,
      });
    },
  };

  public openInfoWindow(marker: MapMarker, markerData: StoryMarkerData): void {
    this.infoWindowStory = markerData.story;
    this.infoWindow.open(marker);
  }

  public closeInfoWindow(): void {
    this.infoWindow.close();
  }

  private requestGeolocation(): void {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          this.userLocation = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          this.userLocationMarker = {
            icon: {
              path: google.maps.SymbolPath.CIRCLE,
              scale: 10,
              fillColor: '#4285F4',
              fillOpacity: 1,
              strokeColor: '#ffffff',
              strokeWeight: 3,
            },
          };
        },
        (error) => {
          console.log('Geolocation permission denied or error:', error);
        },
        {
          enableHighAccuracy: true,
          timeout: 5000,
          maximumAge: 0,
        },
      );
    }
  }

  public openNavigationOptions(): void {
    this.matBottomSheet.open(
      NavigationOptions,
      {
        data: {lat: this.infoWindowStory.latitude, lng: this.infoWindowStory.longitude},
      });
  }

}
